import React, { useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
  CheckCircle as OnlineIcon,
  WatchLater as IdleIcon,
  Cancel as OfflineIcon
} from '@mui/icons-material';
import OpenLayersMapEnhanced from '../components/tracking/OpenLayersMapEnhanced';
import WorkerDetailsPanel from '../components/tracking/WorkerDetailsPanel';
import HistoryControls from '../components/tracking/HistoryControls';
import { useWorkerLocations } from '../hooks/useWorkerLocations';
import trackingApi from '../api/v1/trackingApi';

/**
 * Dashboard mejorado de rastreo de trabajadores
 *
 * Características:
 * - Vista en tiempo real de ubicaciones
 * - Iconos personalizados con animaciones
 * - Historial de ubicaciones por rango de fechas o últimas N posiciones
 * - Visualización de rutas en el mapa
 * - Panel de detalles del trabajador
 */
const TrackingDashboardEnhanced = () => {
  const {
    workers,
    isConnected,
    isLoading,
    error,
    onlineCount,
    idleCount,
    offlineCount
  } = useWorkerLocations();

  const [selectedWorker, setSelectedWorker] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [routeData, setRouteData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Filtrar trabajadores
  const filteredWorkers = workers.filter(worker => {
    const matchesSearch = searchTerm === '' ||
      worker.nombre_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      worker.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      worker.apellido?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || worker.estado === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleWorkerClick = (worker) => {
    setSelectedWorker(worker);
    setShowHistory(false);
    setRouteData(null);
    setHistoryData(null);
  };

  const handleWorkerListClick = (worker) => {
    setSelectedWorker(worker);
  };

  const handleViewHistory = (worker) => {
    setSelectedWorker(worker);
    setShowHistory(true);
    setRouteData(null);
    setHistoryData(null);
  };

  const handleViewRoute = async (worker) => {
    try {
      setLoadingHistory(true);
      setHistoryError(null);

      // Cargar ruta de las últimas 24 horas por defecto
      const fecha_fin = new Date().toISOString();
      const fecha_inicio = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      const response = await trackingApi.getWorkerRoute(
        worker.id_trabajador,
        fecha_inicio,
        fecha_fin
      );

      if (response.success && response.data) {
        setRouteData(response.data);
        setHistoryData(null);
        setShowHistory(false);
        setSnackbar({
          open: true,
          message: `Ruta cargada: ${response.data.total_puntos} puntos, ${response.data.distancia_total_km} km`,
          severity: 'success'
        });
      }
    } catch (err) {
      console.error('Error cargando ruta:', err);
      setHistoryError('Error al cargar la ruta del trabajador');
      setSnackbar({
        open: true,
        message: 'Error al cargar la ruta',
        severity: 'error'
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleLoadHistory = async (params) => {
    try {
      setLoadingHistory(true);
      setHistoryError(null);

      let response;

      if (params.mode === 'lastPositions') {
        // Cargar últimas N posiciones
        const fecha_fin = new Date().toISOString();
        const fecha_inicio = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(); // Últimos 30 días

        response = await trackingApi.getWorkerLocationHistory(
          params.id_trabajador,
          fecha_inicio,
          fecha_fin,
          params.limit
        );
      } else {
        // Cargar por rango de fechas
        response = await trackingApi.getWorkerLocationHistory(
          params.id_trabajador,
          params.fecha_inicio,
          params.fecha_fin,
          1000 // Máximo 1000 puntos
        );
      }

      if (response.success && response.data) {
        setHistoryData(response.data);
        setRouteData(null);
        setSnackbar({
          open: true,
          message: `Historial cargado: ${response.data.ubicaciones?.length || 0} puntos`,
          severity: 'success'
        });
      }
    } catch (err) {
      console.error('Error cargando historial:', err);
      setHistoryError('Error al cargar el historial de ubicaciones');
      setSnackbar({
        open: true,
        message: 'Error al cargar el historial',
        severity: 'error'
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCloseHistory = () => {
    setShowHistory(false);
    setHistoryData(null);
    setRouteData(null);
  };

  const handleCloseDetails = () => {
    setSelectedWorker(null);
    setShowHistory(false);
    setHistoryData(null);
    setRouteData(null);
  };

  const getStatusIcon = (estado) => {
    switch (estado) {
      case 'online':
        return <OnlineIcon sx={{ color: '#4CAF50' }} />;
      case 'idle':
        return <IdleIcon sx={{ color: '#FFC107' }} />;
      default:
        return <OfflineIcon sx={{ color: '#9E9E9E' }} />;
    }
  };

  const getStatusText = (estado) => {
    switch (estado) {
      case 'online':
        return 'En línea';
      case 'idle':
        return 'Inactivo';
      default:
        return 'Desconectado';
    }
  };

  const formatLastUpdate = (segundos) => {
    if (segundos < 60) return 'Hace unos segundos';
    if (segundos < 3600) return `Hace ${Math.floor(segundos / 60)} min`;
    return `Hace ${Math.floor(segundos / 3600)} h`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Rastreo GPS de Trabajadores
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<OnlineIcon />}
            label={`En línea: ${onlineCount}`}
            color="success"
            onClick={() => setStatusFilter('online')}
            variant={statusFilter === 'online' ? 'filled' : 'outlined'}
          />
          <Chip
            icon={<IdleIcon />}
            label={`Inactivos: ${idleCount}`}
            sx={{
              bgcolor: statusFilter === 'idle' ? '#FFC107' : 'transparent',
              color: statusFilter === 'idle' ? '#000' : '#FFC107'
            }}
            onClick={() => setStatusFilter('idle')}
            variant={statusFilter === 'idle' ? 'filled' : 'outlined'}
          />
          <Chip
            icon={<OfflineIcon />}
            label={`Desconectados: ${offlineCount}`}
            onClick={() => setStatusFilter('offline')}
            variant={statusFilter === 'offline' ? 'filled' : 'outlined'}
          />
          {statusFilter !== 'all' && (
            <Chip
              label="Ver todos"
              onClick={() => setStatusFilter('all')}
              variant="outlined"
            />
          )}
        </Box>
        {!isConnected && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Desconectado del servidor. Intentando reconectar...
          </Alert>
        )}
      </Box>

      <Grid container spacing={2}>
        {/* Panel lateral con lista de trabajadores */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '75vh', display: 'flex', flexDirection: 'column' }}>
            <TextField
              fullWidth
              placeholder="Buscar trabajador..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
              size="small"
            />

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : filteredWorkers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                No se encontraron trabajadores
              </Typography>
            ) : (
              <List sx={{ overflow: 'auto', flex: 1 }}>
                {filteredWorkers.map(worker => (
                  <ListItem
                    key={worker.id_trabajador}
                    button
                    selected={selectedWorker?.id_trabajador === worker.id_trabajador}
                    onClick={() => handleWorkerListClick(worker)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: selectedWorker?.id_trabajador === worker.id_trabajador ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemIcon>
                      {getStatusIcon(worker.estado)}
                    </ListItemIcon>
                    <ListItemText
                      primary={worker.nombre_completo || `${worker.nombre} ${worker.apellido}`}
                      secondary={
                        <>
                          <Typography variant="caption" display="block" color={worker.estado === 'online' ? 'success.main' : worker.estado === 'idle' ? 'warning.main' : 'text.secondary'}>
                            {getStatusText(worker.estado)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatLastUpdate(worker.segundos_desde_actualizacion || 0)}
                          </Typography>
                          {worker.velocidad > 0 && (
                            <Typography variant="caption" display="block" fontWeight="medium">
                              🚗 {(worker.velocidad * 3.6).toFixed(1)} km/h
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Mapa */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 0, height: '75vh', overflow: 'hidden', position: 'relative' }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <OpenLayersMapEnhanced
                  workers={workers}
                  selectedWorker={selectedWorker}
                  onWorkerClick={handleWorkerClick}
                  routeData={routeData}
                  historyPoints={historyData?.ubicaciones}
                />

                {/* Panel de detalles del trabajador */}
                {selectedWorker && !showHistory && (
                  <WorkerDetailsPanel
                    worker={selectedWorker}
                    onClose={handleCloseDetails}
                    onViewHistory={handleViewHistory}
                    onViewRoute={handleViewRoute}
                  />
                )}

                {/* Controles de historial */}
                {showHistory && selectedWorker && (
                  <HistoryControls
                    worker={selectedWorker}
                    onClose={handleCloseHistory}
                    onLoadHistory={handleLoadHistory}
                    isLoading={loadingHistory}
                  />
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrackingDashboardEnhanced;
